import * as React from 'react';
const { forwardRef, useEffect, useRef, useState } = React;
const h = React.createElement;
import { icons } from '../icons';
import { inputSizeClasses } from '../shared';

export interface SelectOption {
  label: React.ReactNode;
  value: string | number;
  disabled?: boolean;
}

export interface SelectProps {
  value?: string | number | (string | number)[];
  onChange?: (value: string | number | (string | number)[] | undefined) => void;
  placeholder?: string;
  style?: React.CSSProperties;
  options?: SelectOption[];
  showSearch?: boolean;
  loading?: boolean;
  allowClear?: boolean;
  disabled?: boolean;
  size?: 'small' | 'middle' | 'large';
  className?: string;
  mode?: 'multiple';
  maxTagCount?: number | 'responsive';
}

interface SelectComponent extends React.ForwardRefExoticComponent<
  SelectProps & React.RefAttributes<HTMLDivElement>
> {
  Option: React.FC<{ children?: React.ReactNode; value?: string | number }>;
}

const Select = forwardRef<HTMLDivElement, SelectProps>(function Select(props, ref) {
  const {
    value,
    onChange,
    placeholder,
    style,
    options = [],
    showSearch,
    loading,
    allowClear,
    disabled,
    size = 'middle',
    className = '',
    mode,
    maxTagCount,
  } = props;
  const isMultiple = mode === 'multiple';
  const valueArr: (string | number)[] = isMultiple
    ? (Array.isArray(value) ? value : [])
    : value !== undefined && value !== null ? [value as string | number] : [];
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions =
    showSearch && searchText
      ? options.filter((o) => String(o.label).toLowerCase().includes(searchText.toLowerCase()))
      : options;

  const selectedOption = isMultiple
    ? null
    : options.find((o) => o.value === value);

  const setRefs = (el: HTMLDivElement | null) => {
    containerRef.current = el;
    if (typeof ref === 'function') ref(el);
    else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = el;
  };

  const handleOptionClick = (opt: SelectOption) => {
    if (isMultiple) {
      const newVal = valueArr.includes(opt.value)
        ? valueArr.filter((v) => v !== opt.value)
        : [...valueArr, opt.value];
      if (onChange) onChange(newVal);
    } else {
      if (onChange) onChange(opt.value);
      setOpen(false);
      setSearchText('');
    }
  };

  const handleClear = (e: React.MouseEvent<HTMLSpanElement>) => {
    e.stopPropagation();
    if (onChange) onChange(isMultiple ? [] : undefined);
  };

  const selectedTags = isMultiple
    ? options.filter((o) => valueArr.includes(o.value))
    : [];

  const displayedTags =
    isMultiple && maxTagCount === 'responsive'
      ? selectedTags.slice(0, 3)
      : isMultiple && typeof maxTagCount === 'number'
        ? selectedTags.slice(0, maxTagCount)
        : selectedTags;

  const overflowCount = selectedTags.length - displayedTags.length;

  const displayContent = isMultiple
    ? valueArr.length > 0
      ? h('span', { className: 'flex-1 flex items-center gap-0.5 flex-wrap overflow-hidden max-w-full' },
          ...displayedTags.map((t) =>
            h('span', {
              key: t.value,
              className: 'inline-flex items-center bg-primary-600/20 text-primary-300 text-xs px-1.5 py-0.5 rounded',
            }, t.label),
          ),
          overflowCount > 0 && h('span', { className: 'text-dark-400 text-xs' }, `+${overflowCount}`),
        )
      : h('span', { className: 'flex-1 text-dark-500' }, placeholder || 'Select...')
    : h(
        'span',
        { className: `flex-1 ${selectedOption ? 'text-white' : 'text-dark-500'}` },
        selectedOption ? selectedOption.label : placeholder || 'Select...',
      );

  return h(
    'div',
    { ref: setRefs, className: 'relative', style },
    h(
      'div',
      {
        className: `flex items-center bg-dark-800 border border-dark-700 rounded-lg cursor-pointer hover:border-dark-500 transition-colors ${isMultiple ? 'min-h-[2rem]' : inputSizeClasses[size] || inputSizeClasses.middle} ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${open ? 'ring-2 ring-primary-500/50 border-primary-500' : ''} ${className}`,
        onClick: () => !disabled && setOpen(!open),
      },
      displayContent,
      allowClear &&
        !isMultiple &&
        value !== undefined &&
        h(
          'span',
          { className: 'text-dark-400 hover:text-white mr-1', onClick: handleClear },
          h(icons.CloseCircleOutlined, { size: '0.75em' }),
        ),
      allowClear &&
        isMultiple &&
        valueArr.length > 0 &&
        h(
          'span',
          { className: 'text-dark-400 hover:text-white mr-1', onClick: handleClear },
          h(icons.CloseCircleOutlined, { size: '0.75em' }),
        ),
      h(
        'span',
        { className: 'text-dark-400 ml-1' },
        open ? h(icons.UpOutlined, { size: '0.6em' }) : h(icons.DownOutlined, { size: '0.6em' }),
      ),
    ),
    open &&
      h(
        'div',
        {
          className:
            'absolute z-50 mt-1 w-full bg-dark-800 border border-dark-700 rounded-lg shadow-xl overflow-hidden',
        },
        showSearch &&
          h('input', {
            className:
              'w-full px-3 py-2 bg-dark-900 border-b border-dark-700 text-sm text-white placeholder-dark-500 focus:outline-none',
            placeholder: 'Search...',
            value: searchText,
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => setSearchText(e.target.value),
            autoFocus: true,
          }),
        h(
          'div',
          { className: 'max-h-60 overflow-y-auto py-1' },
          loading
            ? h('div', { className: 'px-3 py-4 text-center text-dark-500' }, 'Loading...')
            : filteredOptions.length === 0
              ? h('div', { className: 'px-3 py-4 text-center text-dark-500' }, 'No data')
              : filteredOptions.map((opt) => {
                  const checked = valueArr.includes(opt.value);
                  return h(
                    'div',
                    {
                      key: opt.value,
                      className: `px-3 py-2 cursor-pointer text-sm transition-colors flex items-center gap-2 ${checked ? 'bg-primary-600/20 text-primary-300' : 'text-dark-200 hover:bg-dark-700'}`,
                      onClick: () => handleOptionClick(opt),
                    },
                    isMultiple &&
                      h('input', {
                        type: 'checkbox',
                        checked,
                        className: 'accent-primary-500',
                        onChange: () => {}, // handled by div onClick
                        onClick: (e: React.MouseEvent<HTMLInputElement>) => e.stopPropagation(),
                      }),
                    opt.label,
                  );
                }),
        ),
      ),
  );
}) as SelectComponent;

Select.Option = function SelectOption(props) {
  return h(React.Fragment, null, props.children) as React.ReactElement;
};

export { Select };
